// src/ui/screens/post/widgets/FilterSheet.jsx

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { propertyCategories } from '../../../../utils/propertyCategories';
import { sortedDistricts, getDistrictsByRegion } from '../../../../utils/districts';
import { getDivisionsForDistrict } from '../../../../utils/divisions';

// ── FilterState class ────────────────────────────────────────────────────────

export class FilterState {
  constructor({
    district = null,
    division = null,
    category = null,
    subCategory = null,
    minPrice = null,
    maxPrice = null,
  } = {}) {
    this.district = district;
    this.division = division;
    this.category = category;
    this.subCategory = subCategory;
    this.minPrice = minPrice;
    this.maxPrice = maxPrice;
  }

  get isEmpty() {
    return this.district === null &&
      this.division === null &&
      this.category === null &&
      this.subCategory === null &&
      this.minPrice === null &&
      this.maxPrice === null;
  }

  get activeCount() {
    let n = 0;
    if (this.district !== null) n++;
    if (this.category !== null || this.subCategory !== null) n++;
    if (this.minPrice !== null || this.maxPrice !== null) n++;
    return n;
  }

  copyWith({
    district = undefined,
    division = undefined,
    category = undefined,
    subCategory = undefined,
    minPrice = undefined,
    maxPrice = undefined,
  } = {}) {
    return new FilterState({
      district: district !== undefined ? district : this.district,
      division: division !== undefined ? division : this.division,
      category: category !== undefined ? category : this.category,
      subCategory: subCategory !== undefined ? subCategory : this.subCategory,
      minPrice: minPrice !== undefined ? minPrice : this.minPrice,
      maxPrice: maxPrice !== undefined ? maxPrice : this.maxPrice,
    });
  }

  matches(listing) {
    // Location
    if (this.district) {
      const loc = (listing.district || listing.location || '').toLowerCase();
      if (!loc.includes(this.district.toLowerCase())) return false;
    }
    if (this.division) {
      const div = (listing.division || listing.location || '').toLowerCase();
      if (!div.includes(this.division.toLowerCase())) return false;
    }

    // Category
    if (this.category) {
      const cat = (listing.category || '').toLowerCase();
      if (!cat.includes(this.category.toLowerCase())) return false;
    }
    if (this.subCategory) {
      const sub = (listing.subcategory || '').toLowerCase();
      if (!sub.includes(this.subCategory.toLowerCase())) return false;
    }

    // Price
    const rawPrice = listing.price || listing.rentPrice || listing.salePrice;
    const price = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice);
    if (this.minPrice !== null && price !== null && price < this.minPrice) return false;
    if (this.maxPrice !== null && price !== null && price > this.maxPrice) return false;

    return true;
  }
}

// ── Filter Icon Button ──────────────────────────────────────────────────────

export const FilterIconButton = ({ current, onApply, size = 22, color }) => {
  const [showSheet, setShowSheet] = useState(false);

  const handleClick = () => {
    setShowSheet(true);
  };

  const handleApply = (result) => {
    setShowSheet(false);
    if (result) onApply(result);
  };

  const count = current.activeCount;

  return (
    <>
      <button
        onClick={handleClick}
        className="relative w-[38px] h-[38px] rounded-lg flex items-center justify-center transition-colors"
        style={{
          backgroundColor: count > 0 ? 'rgba(255,140,8,0.15)' : '#23262C',
          border: `1px solid ${count > 0 ? 'rgba(255,140,8,0.5)' : 'rgba(255,255,255,0.08)'}`,
        }}
      >
        {/* Filter Icon SVG */}
        <svg
          className="w-[22px] h-[22px]"
          viewBox="0 0 24 24"
          fill="none"
          stroke={count > 0 ? '#FF8C08' : (color || 'rgba(255,255,255,0.75)')}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="4" y1="21" x2="4" y2="14" />
          <line x1="4" y1="10" x2="4" y2="3" />
          <line x1="12" y1="21" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12" y2="3" />
          <line x1="20" y1="21" x2="20" y2="16" />
          <line x1="20" y1="12" x2="20" y2="3" />
          <line x1="1" y1="14" x2="7" y2="14" />
          <line x1="9" y1="8" x2="15" y2="8" />
          <line x1="17" y1="16" x2="23" y2="16" />
        </svg>

        {count > 0 && (
          <div className="absolute -right-1 -top-1 w-4 h-4 rounded-full bg-filatom-primary flex items-center justify-center">
            <span className="text-white text-[10px] font-bold">{count}</span>
          </div>
        )}
      </button>

      {showSheet && (
        <FilterSheet
          initial={current}
          onClose={() => setShowSheet(false)}
          onApply={handleApply}
        />
      )}
    </>
  );
};

// ── Filter Sheet ─────────────────────────────────────────────────────────────

const K_MAX_CAP = 5000000000;

const PRICE_PRESETS = [
  { label: 'Any price', min: null, max: null },
  { label: 'Under 500K', min: 0, max: 500000 },
  { label: '500K – 2M', min: 500000, max: 2000000 },
  { label: '2M – 10M', min: 2000000, max: 10000000 },
  { label: '10M – 50M', min: 10000000, max: 50000000 },
  { label: '50M – 200M', min: 50000000, max: 200000000 },
  { label: '200M – 1B', min: 200000000, max: 1000000000 },
  { label: 'Above 1B', min: 1000000000, max: null },
];

const FilterSheet = ({ initial, onClose, onApply }) => {
  const [district, setDistrict] = useState(initial.district);
  const [division, setDivision] = useState(initial.division);
  const [category, setCategory] = useState(initial.category);
  const [subCategory, setSubCategory] = useState(initial.subCategory);
  const [minPrice, setMinPrice] = useState(initial.minPrice || 0);
  const [maxPrice, setMaxPrice] = useState(initial.maxPrice || K_MAX_CAP);
  const [expandedSection, setExpandedSection] = useState(0);
  const [minInput, setMinInput] = useState(initial.minPrice ? String(initial.minPrice) : '');
  const [maxInput, setMaxInput] = useState(initial.maxPrice && initial.maxPrice < K_MAX_CAP ? String(initial.maxPrice) : '');
  const [isVisible, setIsVisible] = useState(false);

  const sheetRef = useRef(null);

  // ── Animation on mount ────────────────────────────────────────────────────

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // ── Close on outside click ──────────────────────────────────────────────

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sheetRef.current && !sheetRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // ── Computed ──────────────────────────────────────────────────────────────

  const getCurrentFilter = () => {
    return new FilterState({
      district,
      division,
      category,
      subCategory,
      minPrice: minPrice > 0 ? minPrice : null,
      maxPrice: maxPrice < K_MAX_CAP ? maxPrice : null,
    });
  };

  const activeCount = getCurrentFilter().activeCount;

  const getLocationSubtitle = () => {
    if (!district) return 'All locations';
    if (!division) return district;
    return `${district} · ${division}`;
  };

  const getCategorySubtitle = () => {
    if (!category) return 'All categories';
    if (!subCategory) return category;
    return `${category} · ${subCategory}`;
  };

  const getPriceSubtitle = () => {
    const hasMin = minPrice > 0;
    const hasMax = maxPrice < K_MAX_CAP;
    if (!hasMin && !hasMax) return 'Any price';
    if (hasMin && hasMax) return `${formatPrice(minPrice)} – ${formatPrice(maxPrice)}`;
    if (hasMin) return `From ${formatPrice(minPrice)}`;
    return `Up to ${formatPrice(maxPrice)}`;
  };

  const formatPrice = (p) => {
    if (p >= 1000000000) return `${(p / 1000000000).toFixed(1)}B`;
    if (p >= 1000000) return `${(p / 1000000).toFixed(0)}M`;
    if (p >= 1000) return `${(p / 1000).toFixed(0)}K`;
    return String(Math.round(p));
  };

  const getDistricts = () => {
    return sortedDistricts.map(d => d.name);
  };

  const getDivisions = (districtName) => {
    return getDivisionsForDistrict(districtName).map(d => d.name);
  };

  const handleReset = () => {
    setDistrict(null);
    setDivision(null);
    setCategory(null);
    setSubCategory(null);
    setMinPrice(0);
    setMaxPrice(K_MAX_CAP);
    setMinInput('');
    setMaxInput('');
  };

  const handleApply = () => {
    onApply(getCurrentFilter());
  };

  // ── Chip component ────────────────────────────────────────────────────────

  const Chip = ({ label, selected, onClick, small = false }) => {
    return (
      <button
        onClick={onClick}
        className={`rounded-full transition-all duration-200 ${
          selected
            ? 'bg-filatom-primary/15 border-filatom-primary/60 text-filatom-primary'
            : 'bg-filatom-card border-white/10 text-white/75'
        } border`}
        style={{
          padding: small ? '6px 10px' : '8px 14px',
          fontSize: small ? '12px' : '13px',
          fontWeight: selected ? 700 : 500,
        }}
      >
        {label}
      </button>
    );
  };

  // ── Section component ────────────────────────────────────────────────────

  const Section = ({ index, title, subtitle, icon, children }) => {
    const expanded = expandedSection === index;

    return (
      <div className="border-b border-white/10">
        <button
          onClick={() => setExpandedSection(expanded ? -1 : index)}
          className="w-full flex items-center gap-3 px-5 py-4 hover:bg-white/5 transition-colors"
        >
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{
              backgroundColor: expanded ? 'rgba(255,140,8,0.15)' : '#23262C',
            }}
          >
            <svg
              className={`w-4.5 h-4.5 ${expanded ? 'text-filatom-primary' : 'text-white/50'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {icon === 'location' && (
                <>
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </>
              )}
              {icon === 'category' && (
                <>
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="3" width="7" height="7" rx="1" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                </>
              )}
              {icon === 'price' && (
                <>
                  <path d="M12 2v20" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </>
              )}
            </svg>
          </div>
          <div className="flex-1 text-left">
            <div className="text-white text-sm font-semibold">{title}</div>
            <div className="text-white/40 text-xs">{subtitle}</div>
          </div>
          <svg
            className={`w-5 h-5 text-white/40 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {expanded && (
          <div className="px-5 pb-4">
            {children}
          </div>
        )}
      </div>
    );
  };

  // ── Render sections ──────────────────────────────────────────────────────

  const renderLocationSection = () => (
    <Section index={0} title="Location" subtitle={getLocationSubtitle()} icon="location">
      <div className="flex flex-wrap gap-2">
        <Chip
          label="All locations"
          selected={!district}
          onClick={() => { setDistrict(null); setDivision(null); }}
        />
      </div>

      <div className="mt-3">
        <div className="text-white/50 text-xs font-semibold tracking-wide mb-2">District</div>
        <div className="flex flex-wrap gap-2">
          {getDistricts().map(d => (
            <Chip
              key={d}
              label={d}
              selected={district === d}
              onClick={() => { setDistrict(d); setDivision(null); }}
            />
          ))}
        </div>
      </div>

      {district && (
        <div className="mt-4">
          <div className="text-white/50 text-xs font-semibold tracking-wide mb-2">
            Division / Town in {district}
          </div>
          <div className="flex flex-wrap gap-2">
            <Chip
              label={`All in ${district}`}
              selected={!division}
              onClick={() => setDivision(null)}
              small
            />
            {getDivisions(district).map(d => (
              <Chip
                key={d}
                label={d}
                selected={division === d}
                onClick={() => setDivision(d)}
                small
              />
            ))}
          </div>
        </div>
      )}
    </Section>
  );

  const renderCategorySection = () => (
    <Section index={1} title="Category" subtitle={getCategorySubtitle()} icon="category">
      <div className="flex flex-wrap gap-2">
        <Chip
          label="All categories"
          selected={!category}
          onClick={() => { setCategory(null); setSubCategory(null); }}
        />
      </div>

      <div className="mt-3">
        <div className="text-white/50 text-xs font-semibold tracking-wide mb-2">Category</div>
        <div className="flex flex-wrap gap-2">
          {propertyCategories.map(cat => (
            <Chip
              key={cat.title}
              label={cat.title}
              selected={category === cat.title}
              onClick={() => { setCategory(cat.title); setSubCategory(null); }}
            />
          ))}
        </div>
      </div>

      {category && (
        <div className="mt-4">
          <div className="text-white/50 text-xs font-semibold tracking-wide mb-2">
            Sub-category in {category}
          </div>
          <div className="flex flex-wrap gap-2">
            <Chip
              label={`All in ${category}`}
              selected={!subCategory}
              onClick={() => setSubCategory(null)}
              small
            />
            {propertyCategories
              .find(c => c.title === category)
              ?.subCategories?.map(s => (
                <Chip
                  key={s}
                  label={s}
                  selected={subCategory === s}
                  onClick={() => setSubCategory(s)}
                  small
                />
              )) || []}
          </div>
        </div>
      )}
    </Section>
  );

  const renderPriceSection = () => (
    <Section index={2} title="Price Range" subtitle={getPriceSubtitle()} icon="price">
      <div className="flex flex-wrap gap-2">
        {PRICE_PRESETS.map((preset) => {
          const isSelected = preset.min === (minPrice > 0 ? minPrice : null) &&
            preset.max === (maxPrice < K_MAX_CAP ? maxPrice : null);
          return (
            <Chip
              key={preset.label}
              label={preset.label}
              selected={isSelected}
              onClick={() => {
                setMinPrice(preset.min || 0);
                setMaxPrice(preset.max || K_MAX_CAP);
                setMinInput(preset.min ? String(preset.min) : '');
                setMaxInput(preset.max && preset.max < K_MAX_CAP ? String(preset.max) : '');
              }}
              small
            />
          );
        })}
      </div>

      <div className="mt-4">
        <div className="text-white/50 text-xs font-semibold tracking-wide mb-2">Custom range (UGX)</div>
        <div className="flex items-center gap-2.5">
          <div className="flex-1">
            <input
              type="text"
              value={minInput}
              onChange={(e) => {
                const val = e.target.value.replace(/,/g, '');
                setMinInput(val);
                const parsed = parseFloat(val);
                setMinPrice(isNaN(parsed) ? 0 : parsed);
              }}
              placeholder="Min price"
              className="w-full h-11 px-3 rounded-lg bg-filatom-card text-white text-sm font-medium outline-none border border-white/10 focus:border-filatom-primary/50"
            />
          </div>
          <span className="text-white/40 text-lg">–</span>
          <div className="flex-1">
            <input
              type="text"
              value={maxInput}
              onChange={(e) => {
                const val = e.target.value.replace(/,/g, '');
                setMaxInput(val);
                const parsed = parseFloat(val);
                setMaxPrice(isNaN(parsed) ? K_MAX_CAP : parsed);
              }}
              placeholder="Max price"
              className="w-full h-11 px-3 rounded-lg bg-filatom-card text-white text-sm font-medium outline-none border border-white/10 focus:border-filatom-primary/50"
            />
          </div>
        </div>
      </div>
    </Section>
  );

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
    >
      <div
        ref={sheetRef}
        className={`w-full max-w-md bg-[#13151A] rounded-t-2xl max-h-[90vh] flex flex-col transform transition-transform duration-300 ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-9 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-2.5">
          <span className="text-white text-lg font-bold">Filter listings</span>
          {activeCount > 0 && (
            <button
              onClick={handleReset}
              className="px-2.5 py-1.5 rounded-full text-xs font-semibold border"
              style={{
                backgroundColor: 'rgba(255,140,8,0.12)',
                borderColor: 'rgba(255,140,8,0.35)',
                color: '#FF8C08',
              }}
            >
              Reset all
            </button>
          )}
        </div>

        <div className="h-px bg-white/10" />

        {/* Scrollable sections */}
        <div className="flex-1 overflow-y-auto">
          {renderLocationSection()}
          {renderCategorySection()}
          {renderPriceSection()}
        </div>

        {/* Apply button */}
        <div className="p-5 pt-3 border-t border-white/10">
          <button
            onClick={handleApply}
            className="w-full h-12 rounded-xl bg-filatom-primary text-white font-bold text-sm transition-all hover:opacity-90 active:scale-98"
          >
            {activeCount > 0 ? `Apply filters (${activeCount})` : 'Show all listings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterSheet;